import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { createOrder, getAddresses, getPaymentMethods, generateYocoCheckout, getUser, decrementProductStock, sendAdminOrderNotification } from '../api/api';
import './Checkout.css';

function CheckoutForm() {
  const { cartItems, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  
  // Auth states
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [showLoginPrompt, setShowLoginPrompt] = useState(true);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'ZA',
    culture: '',
    customCulture: ''
  });
  
  const [shippingCost, setShippingCost] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('yoco');
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);

  useEffect(() => {
    checkUserLogin();
    fetchPaymentMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate shipping cost whenever cart total changes
  useEffect(() => {
    const subtotal = getCartTotal();
    const cost = subtotal >= 799 ? 0 : 99;
    setShippingCost(cost);
  }, [cartItems, getCartTotal]);

  const checkUserLogin = async () => {
    try {
      const userData = await getUser();
      setIsLoggedIn(true);
      setUser(userData);
      setShowLoginPrompt(false);
      // Pre-fill form with user data
      setFormData(prev => ({
        ...prev,
        name: userData.name || '',
        email: userData.email || '',
        phone: userData.phone || ''
      }));
      // Load saved addresses
      const addresses = await getAddresses();
      setSavedAddresses(addresses);
      // Auto-select default address if available
      const defaultAddr = addresses.find(addr => addr.isDefault);
      if (defaultAddr) {
        setSelectedAddressId(defaultAddr._id);
        fillAddressFromSaved(defaultAddr);
      }
    } catch (error) {
      // User not logged in, show guest checkout as default
      setIsLoggedIn(false);
    }
  };

  const fillAddressFromSaved = (address) => {
    setFormData(prev => ({
      ...prev,
      street: address.street || '',
      city: address.city || '',
      state: address.province || '',
      postalCode: address.postalCode || ''
    }));
  };

  const handleAddressSelect = (addressId) => {
    setSelectedAddressId(addressId);
    const address = savedAddresses.find(a => a._id === addressId);
    if (address) {
      fillAddressFromSaved(address);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await getPaymentMethods();
      // Backend may return { methods: [...] } or an array; normalize and ensure each method has an `id` field
      const raw = Array.isArray(response.data) ? response.data : (response.data.methods || []);
      const methods = raw.map(m => ({ ...m, id: m.id || m._id || (m.name && String(m.name).toLowerCase()) }));
      // Filter to only show Yoco payment method
      const yocoMethod = methods.find(m => m.id === 'yoco' && m.enabled !== false);
      const enabled = yocoMethod ? [yocoMethod] : [{ id: 'yoco', name: 'Yoco', description: 'Card payment via Yoco', enabled: true }];
      setAvailablePaymentMethods(enabled);
      setPaymentMethod('yoco');
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      // Set default to Yoco only if API call fails
      setAvailablePaymentMethods([
        { id: 'yoco', name: 'Yoco', description: 'Card payment via Yoco', enabled: true }
      ]);
      setPaymentMethod('yoco');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const generateOrderNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const random = String(Math.floor(Math.random() * 100000)).padStart(5, '0');
    return `ORD-${year}${month}${day}-${random}`;
  };

  // Stripe removed - using Yoco instead

  const handleYocoPayment = async () => {
    setLoading(true);
    try {
      const orderNumber = generateOrderNumber();
      const orderId = `ORD-${Date.now()}`;
      const total = getCartTotal() + shippingCost;

      const checkoutItems = cartItems.map(item => ({
        productId: item._id,
        quantity: item.quantity
      }));

      const response = await generateYocoCheckout(orderId, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone
      }, orderNumber, checkoutItems);

      // Store order data in session for post-payment processing
      sessionStorage.setItem('pendingOrder', JSON.stringify({
        orderNumber,
        orderId,
        customer: formData,
        items: cartItems,
        subtotal: getCartTotal(),
        shipping: shippingCost,
        total: total
      }));

      // Redirect to Yoco hosted checkout
      window.location.href = response.data.redirectUrl;
    } catch (error) {
      alert('Yoco payment failed: ' + error.message);
      setLoading(false);
    }
  };

  // Called after Yoco payment confirmation (via webhook/callback)
  // eslint-disable-next-line no-unused-vars
  const submitOrder = async (paymentId, method = paymentMethod) => {
    try {
      const orderData = {
        customer: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          addressLine1: formData.street,
          city: formData.city,
          state: formData.state,
          postalCode: formData.postalCode,
          country: formData.country
        },
        items: cartItems.map(item => ({
          productId: item._id,
          quantity: item.quantity
        })),
        shippingAddress: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          postalCode: formData.postalCode,
          country: formData.country
        },
        paymentMethod: method,
        paymentId
      };

      const response = await createOrder(orderData);
      
      // Get pending order from session storage
      const pendingOrder = sessionStorage.getItem('pendingOrder');
      const orderInfo = pendingOrder ? JSON.parse(pendingOrder) : null;
      if (orderInfo) {
        // Send admin notification email
        await sendAdminOrderNotification({
          orderNumber: orderInfo.orderNumber,
          customer: orderData.customer,
          items: cartItems,
          subtotal: orderInfo.subtotal,
          shipping: orderInfo.shipping,
          total: orderInfo.total
        }).catch(err => console.error('Failed to send admin notification:', err.message));
        
        // Clear session storage
        sessionStorage.removeItem('pendingOrder');
      }
      
      // Decrement stock for each purchased item (non-blocking). orderId ties
      // this back to the checkout session the backend validated and
      // recorded when the Yoco payment link was created.
      cartItems.forEach(item => {
        decrementProductStock(item._id, item.quantity, orderInfo?.orderId).catch(err =>
          console.error('[Checkout] Stock decrement failed for', item._id, err.message)
        );
      });
      clearCart();
      navigate(`/order-confirmation/${response.data.orderNumber}`);
    } catch (error) {
      alert('Order creation failed: ' + error.message);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    if (paymentMethod === 'yoco') {
      await handleYocoPayment();
    } else {
      alert('Please select a valid payment method');
    }
  };

  // Show login prompt for returning customers
  if (showLoginPrompt && !isLoggedIn) {
    return (
      <div className="checkout-page container">
        <div className="login-prompt-box">
          <h1>Welcome to Checkout</h1>
          
          <div className="login-prompt-content">
            <div className="login-section">
              <h2>👤 Returning Customer?</h2>
              <p>Sign in to use your saved addresses and payment methods for a faster checkout experience.</p>
              <Link to="/login" className="btn-login">
                Sign In
              </Link>
              <div className="info-text-box">
                <p className="info-text">Don't have an account? <Link to="/login">Create one here</Link></p>
              </div>
            </div>

            <div className="divider">
              <span>OR</span>
            </div>

            <div className="guest-section">
              <h2>🛒 Guest Checkout</h2>
              <p>Continue without an account. You can create one after your purchase.</p>
              <button onClick={() => setShowLoginPrompt(false)} className="btn-continue">
                Continue as Guest
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page container">
      <h1>Checkout</h1>
      
      {isLoggedIn && (
        <div className="logged-in-banner">
          <p>✓ Signed in as <strong>{user?.name}</strong> | <button onClick={() => { setIsLoggedIn(false); setUser(null); }} className="logout-link">Sign out</button></p>
        </div>
      )}
      
      <div className="checkout-content">
        <form onSubmit={handlePaymentSubmit} className="checkout-form">
          <div className="form-section">
            <h2>Contact Information</h2>
            <input type="text" name="name" placeholder="Full Name" value={formData.name} onChange={handleInputChange} required />
            <input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleInputChange} required />
            <input type="tel" name="phone" placeholder="Phone" value={formData.phone} onChange={handleInputChange} required />
          </div>

          {isLoggedIn && savedAddresses.length > 0 && (
            <div className="form-section">
              <h2>Select Saved Address</h2>
              <div className="saved-addresses">
                {savedAddresses.map(address => (
                  <label key={address._id} className="address-option">
                    <input 
                      type="radio" 
                      value={address._id} 
                      checked={selectedAddressId === address._id} 
                      onChange={() => handleAddressSelect(address._id)} 
                    />
                    <div className="address-details">
                      <strong>{address.label} {address.isDefault && <span className="badge">Default</span>}</strong>
                      <p>{address.street}, {address.city}, {address.province} {address.postalCode}</p>
                    </div>
                  </label>
                ))}
              </div>
              <p className="form-note">Or enter a new address below</p>
            </div>
          )}

          <div className="form-section">
            <h2>Shipping Address</h2>
            <input type="text" name="street" placeholder="Street Address" value={formData.street} onChange={handleInputChange} required />
            <input type="text" name="city" placeholder="City" value={formData.city} onChange={handleInputChange} required />
            <input type="text" name="state" placeholder="State/Province" value={formData.state} onChange={handleInputChange} required />
            <input type="text" name="postalCode" placeholder="Postal Code" value={formData.postalCode} onChange={handleInputChange} required />
            <select name="country" value={formData.country} onChange={handleInputChange} required>
              <option value="ZA">South Africa</option>
              <option value="BW">Botswana</option>
              <option value="NA">Namibia</option>
              <option value="US">United States</option>
              <option value="GB">United Kingdom</option>
              <option value="AU">Australia</option>
            </select>
          </div>

          <div className="form-section">
            <h2>Cultural Preference (Optional)</h2>
            <p className="form-note">Help us personalize your experience</p>
            <select 
              name="culture" 
              value={formData.culture} 
              onChange={handleInputChange}
            >
              <option value="">Select your culture (optional)</option>
              <option value="Zulu">Zulu</option>
              <option value="Xhosa">Xhosa</option>
              <option value="Afrikaans">Afrikaans</option>
              <option value="English">English</option>
              <option value="Sotho">Sotho</option>
              <option value="Tswana">Tswana</option>
              <option value="Pedi">Pedi</option>
              <option value="Tsonga">Tsonga</option>
              <option value="Swati">Swati</option>
              <option value="Venda">Venda</option>
              <option value="Ndebele">Ndebele</option>
              <option value="San (Haiom)">San (Haiom)</option>
              <option value="Nama (Khoekhoe)">Nama (Khoekhoe)</option>
              <option value="Coloured">Coloured</option>
              <option value="Indian">Indian</option>
              <option value="Other">Other (please specify below)</option>
            </select>
            
            {(formData.culture === 'Other' || formData.culture === '') && (
              <input 
                type="text" 
                name="customCulture" 
                placeholder="Enter your culture" 
                value={formData.customCulture} 
                onChange={handleInputChange}
                className="custom-culture-input"
              />
            )}
          </div>

          <div className="form-section">
            <h2>Payment Method</h2>
            <div className="payment-options">
              {availablePaymentMethods.map(method => (
                <label key={method.id} className="payment-option">
                  <input 
                    type="radio" 
                    name="paymentMethod"
                    value={method.id} 
                    checked={paymentMethod === method.id} 
                    onChange={(e) => setPaymentMethod(e.target.value)} 
                  />
                  <div className="payment-method-info">
                    <strong>{method.name}</strong>
                    <small>{method.description}</small>
                  </div>
                </label>
              ))}
            </div>



            {paymentMethod === 'yoco' && (
              <div className="payment-note">
                <p>✓ Secure payment via Yoco</p>
                <p>You will be redirected to Yoco to complete your payment.</p>
                <div className="yoco-options">
                  <p className="payment-option-label">Or pay directly:</p>
                  <a 
                    href="https://pay.yoco.com/mashaba-enterprises" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="yoco-direct-link"
                  >
                    🔗 Open Yoco Payment Form
                  </a>
                </div>
              </div>
            )}
          </div>

          <button type="submit" disabled={loading} className="place-order-btn">
            {loading ? 'Processing...' : 'Place Order'}
          </button>
        </form>

        <div className="order-summary">
          <h2>Order Summary</h2>
          {cartItems.map(item => (
            <div key={item._id} className="summary-item">
              <span>{item.name} x {item.quantity}</span>
              <span>R{((item.price || 0) * item.quantity).toFixed(2)}</span>
            </div>
          ))}
          <div className="summary-row">
            <span>Subtotal:</span>
            <span>R{(getCartTotal() || 0).toFixed(2)}</span>
          </div>
          <div className="summary-row">
            <span>Shipping:</span>
            <span>R{(shippingCost || 0).toFixed(2)}</span>
          </div>
          <div className="summary-row total">
            <span>Total:</span>
            <div>
              <span>R{((getCartTotal() || 0) + (shippingCost || 0)).toFixed(2)}</span>
              <p style={{ fontSize: '0.85em', margin: '4px 0 0 0', color: '#666' }}>(Incl 15% VAT)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckoutForm;